import { LogoutSession } from './logout-session';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { TokenData } from '../models/token-data.model';
import { API } from 'src/app/core/constant/api.constants';
import { ToastService } from 'src/app/shared/toast/services/toast-service';
import { LOCAL_STORAGE } from 'src/app/core/constant/local-storage.constants';
import { PasswordSetup } from '../models/password-setup.model';
import { environment } from 'src/environments/environment';
import { UserManager, UserManagerSettings, User } from 'oidc-client';
import { Token } from 'prismjs';
import { Keepalive } from '@ng-idle/keepalive';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService
{
    public ready: Promise<void>;
    private logoutSession: LogoutSession;
    private unsubscribe: Subscription[] = [];
    private _authNavStatusSource = new BehaviorSubject<boolean>(false);
    // Observable navItem stream
    authNavStatus$ = this._authNavStatusSource.asObservable();

    public manager = new UserManager(getClientSettings());
    public user: User | null;

    isLoading$: Observable<boolean>;
    isLoadingSubject: BehaviorSubject<boolean>;

    token$: Observable<TokenData>;
    tokenSubject: BehaviorSubject<TokenData>;

    get tokenValue(): TokenData
    {
        return this.tokenSubject.value;
    }

    set tokenValue(tokenData: TokenData)
    {
        this.tokenSubject.next(tokenData);
    }

    constructor(
        private http: HttpClient,
        private router: Router,
        private toastService: ToastService,
        private idle: Idle,
        private keepalive: Keepalive
    ) {
        this.isLoadingSubject = new BehaviorSubject<boolean>(false);
        this.isLoading$ = this.isLoadingSubject.asObservable();
        this.tokenSubject = new BehaviorSubject<TokenData>(this.getAuthFromLocalStorage());
        this.token$ = this.tokenSubject.asObservable();
        this.logoutSession = new LogoutSession(this.manager, () => {
            this.user = null;
            this.tokenSubject.next(null);
            this._authNavStatusSource.next(false);
        }, () => { void this.router.navigateByUrl('/signed-out', { replaceUrl: true }); });
        this.ready = this.manager.getUser().then(user => {
            if (this.logoutSession.active) return;
            this.user = user;
            this.tokenSubject.next(user);
            this._authNavStatusSource.next(this.isAuthenticated());
        });

        // Event-driven silent renew on token expiring
        this.manager.events.addAccessTokenExpiring(() => {
            // Perform silent renew
            if (this.user && !this.user.expired) {
                this.manager.signinSilent().then(user => {
                    if (this.logoutSession.active) return;
                    this.user = user;
                    this.setAuthFromLocalStorage(user);
                    this.tokenSubject.next(user);
                }).catch(err => {
                    if (this.logoutSession.active) return;
                    console.error('Silent renew failed:', err);
                });
            }
        });
        this.idle.setIdle(1980);
        this.idle.setTimeout(1);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        this.idle.onTimeout.subscribe(() => {
            if (this.user && !this.user.expired) {
                this.manager.signinSilent().then(user => {
                    if (this.logoutSession.active) return;
                    this.user = user;
                    this.setAuthFromLocalStorage(user);
                    this.tokenSubject.next(user);
                }).catch(err => {
                    if (this.logoutSession.active) return;
                    console.error(' Idle silent renew failed:', err);
                });
            } else {
                console.log(' No valid user session for idle silent renew.');
            }
            this.idle.watch();
        });
        this.idle.watch();
    }
    async login(prompt?: 'login') {
        this.logoutSession.resume();
        await this.manager.signinRedirect(prompt ? { prompt } : undefined);
        this.manager.getUser().then(user => { 
            this.user = user;      
            this._authNavStatusSource.next(this.isAuthenticated());
        }); 
        return   
    }

    async completeAuthentication() {
        this.user = await this.manager.signinRedirectCallback();
        if (this.logoutSession.active) {
            await this.clearSession();
            throw new Error('Login was interrupted by logout');
        }
        this._authNavStatusSource.next(this.isAuthenticated()); 
        this.setAuthFromLocalStorage(this.user);
        this.tokenSubject.next(this.user);
    }  

    // register(userRegistration: any) {    
    // return this.http.post(this.configService.authApiURI + '/account', userRegistration).pipe(catchError(this.handleError));
    // }

    isAuthenticated(): boolean {
    return this.user != null && !this.user.expired;
    }

    // get token(): string {
    //     return `${this.user.access_token}`;
    //     }

    get authorizationHeaderValue(): string {
    return `${this.user.token_type} ${this.user.access_token}`;
    }

    get name(): string {
    return this.user != null ? this.user.profile.name : '';
    }

    async signout() {
    await this.logout();
    }  


    refreshToken(passwordSetup: PasswordSetup): Observable<boolean>
    {
        this.isLoadingSubject.next(true);
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', `Bearer ${ passwordSetup.token }`);
        return this.http.post<any>(API.AUTH.REFRESH, passwordSetup,{
            headers: headers,
          })
            .pipe(
                map((res: any) =>
                {
                    if (this.logoutSession.active) return undefined;
                    const result = res;
                    if (res)
                    {
                        this.setAuthFromLocalStorage(res);
                        this.tokenSubject.next(res);
                    }
                    return result;
                }),
                // switchMap(() => this.getUserByToken() as any),
                catchError((err) =>
                {
                    // this.toastService.error(err);
                    return of(undefined);
                }),
                finalize(() => this.isLoadingSubject.next(false))
            );
    }

    logout()
    {
        return this.logoutSession.start();
    }

    getAuthFromLocalStorage(): TokenData
    {
        try
        {
            const authData = JSON.parse(localStorage.getItem(LOCAL_STORAGE.TOKEN));
            return authData;
        }
        catch (error)
        {
          
        }
        return null;
    }

    private setAuthFromLocalStorage(tokenData: TokenData): boolean {
        if (tokenData && tokenData.access_token) {
            localStorage.setItem(LOCAL_STORAGE.TOKEN, JSON.stringify(tokenData));
            return true;
        }
        return false;
    }

    ngOnDestroy()
    {
        this.logoutSession.dispose();
        this.unsubscribe.forEach((sb) => sb.unsubscribe());
    }

    completeLogout() { return this.logoutSession.complete(); }
    clearSession(notify = true) { return this.logoutSession.clear(notify); }
    frontChannelLogout() { return this.logoutSession.frontChannel(); }

}

export function getClientSettings(): UserManagerSettings{
    return{
        authority: environment.identityServiceUrl,
        client_id: 'claims',
        redirect_uri:  environment.baseurl+'/signin-oidc',
        post_logout_redirect_uri : environment.baseurl+'/signout-callback-oidc',
        response_type:"id_token token",
        scope: "openid profile dauniversal da offline_access",
        filterProtocolClaims: true,
        loadUserInfo: true,
        monitorSession: true,
        automaticSilentRenew: true,
        silent_redirect_uri: environment.baseurl+'/silent-renew.html'
    };
}